/**
 * La identidad la fija el TOKEN. `archon_user` es sólo una caché de su perfil.
 *
 * Antes había dos fuentes sin nadie que las comparase: lo que se pintaba salía
 * de `archon_user` (memoria de React, fijada una vez al arrancar) y lo que se
 * enviaba salía de `localStorage.token`, leído en cada petición. Podían ser
 * personas distintas, y el 16-ago-2026 lo fueron durante doce minutos.
 *
 * Aquí vive la única regla que lo impide: **una caché de perfil sólo vale si su
 * `username` es el `sub` del token que se está enviando.** Si no hay token, si
 * no se puede decodificar, o si no coinciden, la caché se descarta.
 */

/** `sub` del JWT, sin verificar la firma — eso es cosa del servidor. */
export function usernameFromToken(tok) {
  if (!tok || typeof tok !== 'string') return null;
  try {
    const mid = tok.split('.')[1];
    if (!mid) return null;
    const normalized = mid.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized));
    return typeof payload?.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

/** `tenant` del JWT. Sirve para detectar un token de otro tenant. */
export function tenantFromToken(tok) {
  if (!tok || typeof tok !== 'string') return null;
  try {
    const mid = tok.split('.')[1];
    if (!mid) return null;
    const payload = JSON.parse(atob(mid.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload?.tenant === 'string' ? payload.tenant : null;
  } catch {
    return null;
  }
}

/**
 * ¿Este perfil cacheado describe a quien firma el token?
 *
 * Comparación insensible a mayúsculas porque el `sub` viene del formulario de
 * login y `users.username` de la base; en producción se ha visto `Ciocan` y
 * `ciocan` para la misma cuenta.
 */
export function cacheMatchesToken(cached, tok) {
  const sub = usernameFromToken(tok);
  if (!sub) return false;
  const nombre = cached && typeof cached === 'object' ? cached.username : null;
  if (typeof nombre !== 'string' || !nombre) return false;
  return nombre.trim().toLowerCase() === sub.trim().toLowerCase();
}
